import { Component, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import { DatePipe } from "@angular/common";

import { MatPaginator, MatPaginatorIntl, PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";

import moment from "moment";
import * as XLSX from "xlsx";

import { PatientsService } from "../patients.service";
import { AppointmentsService } from "../appointments.service";
import { LanguageService } from "../language.service";
import { LoginService } from "../login.service";
import { SettingsService } from "../settings.service";
import { TranslateService } from "@ngx-translate/core";

type VisitSource = "whatsapp" | "clinic_site" | "direct";
type TabType = "Scheduled Appointments" | "Completed Appointments" | "Not Completed Appointments" | "Room Planning";

@Component({
    selector: "app-appt-stats",
    templateUrl: "./appt-stats.component.html",
    styleUrls: ["./appt-stats.component.scss"],
    standalone: false
})
export class ApptStatsComponent implements OnInit {
  @Output() pageChange = new EventEmitter<string>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Filters
  view: "All" | "Secretary" | "Patient" = "All";
  tab: TabType = "Scheduled Appointments";
  sourceFilter: VisitSource | "all" = "all"; // applies only to patient-made appts (All/Patient views)
  sourceOptions = [
    { value: "all" as const, label: "All sources" },
    { value: "whatsapp" as const, label: "WhatsApp" },
    { value: "clinic_site" as const, label: "Clinic Site" },
    { value: "direct" as const, label: "Direct" },
  ];

  examTypeOptions: Array<{ label: string; value: number | "ALL" }> = [{ label: "All", value: "ALL" }];
  examTypeFilter: number | "ALL" = "ALL";

  // Fund (Kupa/Private) filter - client-side, applied to the current page only
  fundFilter: "all" | "Private" | string = "all";
  get fundOptions(): Array<{ value: string; label: string }> {
    return [
      { value: "all", label: this.translate.instant("appointments.gui.labels.appointment_stats.All") },
      { value: "Private", label: this.translate.instant("appointments.gui.labels.Private") },
      ...Object.keys(this.settingsService.hmoMapping)
        .filter((key) => key !== "Private")
        .map((key) => ({ value: key, label: this.settingsService.hmoMapping[key] })),
    ];
  }

  // Dates
  filterFromDate: Date | null = new Date();
  filterToDate: Date | null = new Date();

  // Data
  allAppointments: any[] = []; // raw page data from server
  filteredAppointments: any[] = []; // after applying view/source filter
  examsTypes = [];
  customExamTypes = [];
  // Stats
  stats = {
    total: 0,
    secretary: 0,
    patient: 0,
    patientWhatsapp: 0,
    patientClinicSite: 0,
    patientDirect: 0,
  };

  // Table config
  // index | aptTime | createdDate | patientID | (doctor | room) | aptlength | (visitSource) | examType | createdBy | Status | LastUpdate
  displayedColumns: string[] = [];

  // Pagination (server-side)
  pageSize = 25;
  pageIndex = 0; // zero-based
  lastPageCount = 0; // rows returned in the last fetch
  totalAppts = 0; // total rows across all pages (from server)

  get hasNextPage(): boolean {
    // if last fetch < pageSize = no next page
    return this.lastPageCount === this.pageSize;
  }
  get hasPrevPage(): boolean {
    return this.pageIndex > 0;
  }

  // Sorting (client-side)
  sortActive: "aptTime" | "createdDate" | "" = "aptTime";
  sortDirection: "asc" | "desc" | "" = "asc";

  // UI state
  loading = false;
  pageDirection = "ltr";
  total = 0; // current filtered count (this page)

  // Room Planning tab state
  roomPlanningRID: number | null = null;
  roomPlanningFromDate: Date | null = new Date();
  roomPlanningToDate: Date | null = new Date();
  roomPlanningResult: {
    totalSlots: number;
    scheduledAppointments: number;
    freeSlots: number;
    utilizationPct: number;
    dailyBreakdown: Array<{ date: string; availableSlots: number; scheduledAppointments: number; freeSlots: number }>;
  } | null = null;
  roomPlanningLoading = false;

  constructor(
    public patientService: PatientsService,
    public loginService: LoginService,
    public appointmentService: AppointmentsService,
    public languageService: LanguageService,
    public settingsService: SettingsService,
    public translate: TranslateService,
    private datePipe: DatePipe,
    private paginatorIntl: MatPaginatorIntl
  ) {}

  async ngOnInit() {
    this.pageDirection = this.languageService.getPageDirection();
    this.configurePaginatorLabels();
    this.buildDisplayedColumns();
    await this.searchAppointmetns();
    await this.getExamTypes();
  }

  // Getters
  get hasResults(): boolean {
    return !!this.filteredAppointments?.length;
  }

  // UI Handlers

  async onViewChange() {
    // Hide/ignore source filter for Secretary view
    if (this.view === "Secretary") {
      this.sourceFilter = "all";
    }

    this.buildDisplayedColumns();

    // Re-query to respect server-side status filter
    if (this.filterFromDate && this.filterToDate) {
      await this.searchAppointmetns();
    }
  }

  async onTabChange() {
    // Reset paging on tab switch and re-run search if dates are set
    this.pageIndex = 0;
    if (this.tab !== "Room Planning" && this.filterFromDate && this.filterToDate) {
      await this.searchAppointmetns();
    }
  }

  async searchRoomPlanning(): Promise<void> {
    if (!this.roomPlanningRID || !this.roomPlanningFromDate || !this.roomPlanningToDate) return;
    this.roomPlanningLoading = true;
    try {
      const from = moment(this.roomPlanningFromDate).format("YYYY-MM-DD");
      const to   = moment(this.roomPlanningToDate).format("YYYY-MM-DD");
      this.roomPlanningResult = await this.appointmentService.getRoomPlanning(this.roomPlanningRID, from, to);
    } catch (e) {
      console.error(e);
      this.roomPlanningResult = null;
    } finally {
      this.roomPlanningLoading = false;
    }
  }

  async onSourceChange() {
    // Source filter triggers server-side query
    this.pageIndex = 0;
    if (this.filterFromDate && this.filterToDate) {
      await this.searchAppointmetns();
    }
  }

  onFundChange() {
    // Client-side filter over the already-fetched page - no re-fetch needed
    this.applyFilters();
  }

  async onExamTypeChange() {
    // Exam type filter is now server-side, so re-fetch data
    this.pageIndex = 0;
    if (this.filterFromDate && this.filterToDate) {
      await this.searchAppointmetns();
    }
  }

  async onMatPage(e: PageEvent) {
    const sizeChanged = e.pageSize !== this.pageSize;
    this.pageSize = e.pageSize;
    const nextIndex = sizeChanged ? 0 : e.pageIndex;
    await this.loadPage(nextIndex);
    if (this.paginator && sizeChanged) {
      this.paginator.firstPage();
    }
  }

  onMatSort(e: Sort) {
    // If user clears sort then default to aptTime asc
    this.sortActive = (e.active as any) || "aptTime";
    this.sortDirection = (e.direction as any) || "asc";
    this.applyCurrentSort();
  }

  setPage(appointment: any) {
    this.pageChange.emit(appointment);
  }

  // Search / Paging

  async searchAppointmetns(): Promise<void> {
    return this.searchAppointments();
  }

  /** Clear entrypoint for searches; resets paging then loads first page. */
  async searchAppointments(): Promise<void> {
    if (!this.filterFromDate || !this.filterToDate) {
      return;
    }
    this.pageIndex = 0;
    await this.loadPage(0);
  }

  // Fetch one page from server (status/date filtered on backend)
  // then apply local filters (view/source) + current sort + stats.
  async loadPage(index: number): Promise<void> {
    if (!this.filterFromDate || !this.filterToDate) return;

    this.loading = true;
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const [res] = await Promise.all([
        this.fetchPageData(index),
        minLoadingTime
      ]);
      this.applyResponse(res, index);
    } catch (error) {
      console.error(error);
      await minLoadingTime;
      this.applyResponse({ rows: [], totalAppts: 0 }, index);
    } finally {
      this.loading = false;
    }
  }

  private async fetchPageData(
    index: number,
    pageSize = this.pageSize
  ): Promise<{ rows: any[]; totalAppts: number }> {
    const fromStr = moment(this.filterFromDate).format("YYYY-MM-DD");
    const toStr = moment(this.filterToDate).format("YYYY-MM-DD");
    const limit = pageSize;
    const offset = index * limit;
    const source = this.view !== "Secretary" && this.sourceFilter !== "all" ? this.sourceFilter : undefined;

    if (this.tab === "Completed Appointments") {
      return await this.appointmentService.searchCompletedAppointments(
          this.statusParam(),
          fromStr,
          toStr,
          limit,
          offset,
          this.examTypeFilter,
          source
        );
    } else if (this.tab === "Not Completed Appointments") {
      return await this.appointmentService.searchNotCompletedAppointments(
          this.statusParam(),
          fromStr,
          toStr,
          limit,
          offset,
          this.examTypeFilter,
          source
        );
    } else {
      return await this.appointmentService.searchAppointmentsByCreator(
          this.statusParam(),
          fromStr,
          toStr,
          limit,
          offset,
          this.examTypeFilter,
          source
        );
    }
  }

  // Apply a page response to component state, then run filters/sort/stats.
  private applyResponse(res: { rows: any[]; totalAppts: number } | null | undefined, index: number) {
    this.pageIndex = index;
    this.totalAppts = res?.totalAppts ?? 0;
    this.allAppointments = Array.isArray(res?.rows)
      ? res.rows.map(({ totalAppts, ...rest }) => rest) // strip helper column
      : [];
    this.lastPageCount = this.allAppointments.length;

    this.applyFilters(); // view/source filter + current sort + stats + columns
  }

  // Misc actions

  clearDateFilter(): void {
    const today = new Date();
    this.filterFromDate = new Date(today);
    this.filterToDate = new Date(today);

    this.allAppointments = [];
    this.filteredAppointments = [];
    this.totalAppts = 0;
    this.total = 0;
    this.updateStats([]);
  }

  // Export all matches (across pages).
  // Fetch in chunks via the same fetcher, then apply the same local filters + current sort.
  async exportExcel() {
    if (!this.filterFromDate || !this.filterToDate || !this.totalAppts) {
      console.warn("Nothing to export");
      return;
    }

    // 1. Fetch all pages using the same fetcher used by the table
    const CHUNK = Math.max(this.pageSize, 1000);
    const allRows: any[] = [];
    let page = 0;

    while (true) {
      const res = await this.fetchPageData(page, CHUNK);
      const rows = Array.isArray(res?.rows) ? res.rows : [];
      allRows.push(...rows.map(({ totalAppts, ...rest }) => rest));
      if (!rows.length || allRows.length >= (res?.totalAppts ?? 0)) break;
      page += 1;
    }

    // 2. Apply the same local filters as the table (view filter)
    // Note: source filter is now done server-side, so already applied in fetched data
    let list = [...allRows];
    if (this.view === "Secretary") {
      list = list.filter((r) => !r?.CreatedByPID);
    } else if (this.view === "Patient") {
      list = list.filter((r) => !!r?.CreatedByPID);
    }

    // Exam type filtering is now done server-side, removed from client-side export

    // 3. Apply the current sort (aptTime/createdDate, asc/desc)
    const order = (this.sortDirection || "asc") as "asc" | "desc";
    const mult = order === "asc" ? 1 : -1;
    const ts = (v: any) => {
      const d = v instanceof Date ? v : new Date(v);
      const t = d?.getTime?.();
      return Number.isFinite(t) ? t : 0;
    };
    if (this.sortActive === "createdDate") {
      list.sort((a, b) => {
        const diff = ts(a.createdDate) - ts(b.createdDate);
        if (diff !== 0) return mult * diff;
        return mult * ((a.AutoIndex ?? 0) - (b.AutoIndex ?? 0));
      });
    } else {
      list.sort((a, b) => {
        const diff = ts(a.aptTime) - ts(b.aptTime);
        if (diff !== 0) return mult * diff;
        return mult * ((a.AutoIndex ?? 0) - (b.AutoIndex ?? 0));
      });
    }

    // 4. Calculate totals for summary
    const exportStats = {
      total: list.length,
      secretary: list.filter((r) => !r?.CreatedByPID).length,
      patient: list.filter((r) => !!r?.CreatedByPID).length,
      patientWhatsapp: list.filter(
        (r) => !!r?.CreatedByPID && this.normalizeSource(r?.visitSource) === "whatsapp"
      ).length,
      patientClinicSite: list.filter(
        (r) => !!r?.CreatedByPID && this.normalizeSource(r?.visitSource) === "clinic_site"
      ).length,
      patientDirect: list.filter(
        (r) => !!r?.CreatedByPID && this.normalizeSource(r?.visitSource) === "direct"
      ).length,
    };

    // 5. Map to export rows (same displayed columns + summary)
    const byRoom = this.loginService?.premissions?.ApptsByRoom;
    const exportRows = list.map((row, index) => {
      const aptTime = this.datePipe.transform(row.aptTime, "dd/MM/yyyy HH:mm") || "";
      const createdTime = this.datePipe.transform(row.createdDate, "dd/MM/yyyy HH:mm") || "";
      const lastUpdate = row.LastUpdate
        ? this.datePipe.transform(row.LastUpdate, "dd/MM/yyyy HH:mm") || ""
        : "";

      const base: any = {
        [this.translate.instant("appointments.gui.labels.appointment_stats.table.Appointment Time")]: aptTime,
        [this.translate.instant("appointments.gui.labels.appointment_stats.table.Creation Time")]: createdTime,
        [this.translate.instant("appointments.gui.labels.appointment_stats.table.Patient ID")]: row.patientID ?? "",
        [this.translate.instant("appointments.gui.labels.appointment_stats.table.Length (min)")]: row.aptlength ?? "",
        [this.translate.instant("appointments.gui.labels.appointment_stats.table.Created By")]: this.createdByLabel(row),
        [this.translate.instant("appointments.gui.labels.appointment_stats.table.Exam Type")]: row.examType ?? "",
        [this.translate.instant("appointments.gui.labels.Payer")]: this.fundingLabel(row),
        [this.translate.instant("appointments.gui.labels.Exam Code")]: row.examCode ?? "",
        "סומן כבוצע": row.completed ? "כן" : "לא",
        [this.translate.instant("appointments.gui.labels.appointment_stats.table.Last Update")]: lastUpdate,
      };
      if (byRoom) {
        base[this.translate.instant("appointments.gui.labels.appointment_stats.table.Room")] = row.roomName ?? "";
      } else {
        base[this.translate.instant("appointments.gui.labels.appointment_stats.table.Doctor")] = row.doctorName ?? "";
      }
      if (this.view !== "Secretary") {
        base[this.translate.instant("appointments.gui.labels.appointment_stats.table.Source")] = this.sourceLabel(row.visitSource);
      }

      // Add summary columns (only on first row)
      if (index === 0) {
        base[""] = ""; // Empty column separator
        base[this.translate.instant("appointments.gui.labels.appointment_stats.Total")] = exportStats.total;
        base[this.translate.instant("appointments.gui.labels.appointment_stats.By Secretary")] = exportStats.secretary;
        base[this.translate.instant("appointments.gui.labels.appointment_stats.By Patient")] = exportStats.patient;
        base[`${this.translate.instant("appointments.gui.labels.appointment_stats.By Patient")} (${this.translate.instant("appointments.gui.labels.appointment_stats.WhatsApp")})`] = exportStats.patientWhatsapp;
        base[`${this.translate.instant("appointments.gui.labels.appointment_stats.By Patient")} (${this.translate.instant("appointments.gui.labels.appointment_stats.Clinic Site")})`] = exportStats.patientClinicSite;
        base[`${this.translate.instant("appointments.gui.labels.appointment_stats.By Patient")} (${this.translate.instant("appointments.gui.labels.appointment_stats.Direct")})`] = exportStats.patientDirect;
      }

      return base;
    });

    // 6. Write XLSX
    const fromLabel = this.datePipe.transform(this.filterFromDate, "dd-MM-yyyy") || "";
    const toLabel = this.datePipe.transform(this.filterToDate, "dd-MM-yyyy") || "";
    const scope = fromLabel && toLabel ? `from ${fromLabel} to ${toLabel}` : "all";

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Appointments");
    const fileName = `appointments_${scope}.xlsx`;

    // console.log("[export] writing file", { rows: exportRows.length, fileName });
    XLSX.writeFile(wb, fileName);
  }

  // Template helpers:
  createdByLabel(row: any): string {
    const key = row?.CreatedByPID ? "Patient" : "Secretary";
    return this.translate.instant(`appointments.gui.labels.appointment_stats.${key}`);
  }

  sourceLabel(vistiSource?: string): string {
    const label = this.normalizeSource(vistiSource);
    switch (label) {
      case "whatsapp":
        return this.translate.instant("appointments.gui.labels.appointment_stats.WhatsApp");
      case "clinic_site":
        return this.translate.instant("appointments.gui.labels.appointment_stats.Clinic Site");
      case "direct":
        return this.translate.instant("appointments.gui.labels.appointment_stats.Direct");
      default:
        return "-";
    }
  }

  // The appointment only stores Funding = 'Private' | 'Hmo'; when it's an HMO
  // booking, the specific fund is the patient's own HMO on file (patientHMO).
  fundingLabel(row: any): string {
    if (!row?.Funding) {
      return "-";
    }
    if (row.Funding === "Private") {
      return this.translate.instant("appointments.gui.labels.Private");
    }
    return row.patientHMO
      ? this.settingsService.hmoMapping[row.patientHMO] || row.patientHMO
      : this.translate.instant("appointments.gui.labels.HMO");
  }

  // HMO booking with no matching fund on the patient's record - flags exactly
  // the "booked for something they're not entitled to" scenario.
  isFundingMismatch(row: any): boolean {
    return row?.Funding === "Hmo" && !row?.patientHMO;
  }

  // Filtering / Columns:
  private statusParam(): "all" | "secretary" | "patient" {
    switch (this.view) {
      case "Secretary":
        return "secretary";
      case "Patient":
        return "patient";
      default:
        return "all";
    }
  }

  // Apply filters (view + source), then current sort, stats, and columns.
  private applyFilters() {
    let list = [...(this.allAppointments || [])];

    // View filter:
    if (this.view === "Secretary") {
      list = list.filter((appt) => !appt?.CreatedByPID);
    } else if (this.view === "Patient") {
      list = list.filter((appt) => !!appt?.CreatedByPID);
    }

    // Source filter is now handled server-side

    // Fund filter (client-side, applied to the current page only)
    if (this.fundFilter !== "all") {
      if (this.fundFilter === "Private") {
        list = list.filter((appt) => appt?.Funding === "Private");
      } else {
        list = list.filter((appt) => appt?.Funding === "Hmo" && appt?.patientHMO === this.fundFilter);
      }
    }

    // Exam type filtering is now done server-side, removed from client-side

    list = list.map((r) => ({
      ...r,
      examType: (r?.examName ?? r?.examType ?? "").toString().trim(),
    }));

    // Create a new array reference to ensure Angular's change detection picks up the change
    this.filteredAppointments = [...list];
    this.total = list.length;

    this.applyCurrentSort();
    this.updateStats(list);
    this.buildDisplayedColumns();
  }

  // Recalculate top counters for the currently filtered list.
  private updateStats(list: any[] = []) {
    const s = {
      total: 0,
      secretary: 0,
      patient: 0,
      patientWhatsapp: 0,
      patientClinicSite: 0,
      patientDirect: 0,
    };

    for (const row of list) {
      s.total += 1;
      if (!row?.CreatedByPID) {
        s.secretary += 1;
        continue;
      }
      s.patient += 1;
      const src = this.normalizeSource(row?.visitSource);
      if (src === "whatsapp") {
        s.patientWhatsapp += 1;
      } else if (src === "clinic_site") {
        s.patientClinicSite += 1;
      } else if (src === "direct") {
        s.patientDirect += 1;
      }
    }

    this.stats = s;
  }

  // Build columns dynamically based on permissions (Doctor vs Room) and view (Source column).
  private buildDisplayedColumns() {
    const cols: string[] = ["index", "aptTime", "createdDate", "patientID", "examType"];
    if (this.loginService?.premissions?.ApptsByRoom) {
      cols.push("roomName");
    } else {
      cols.push("doctorName");
    }
    cols.push("aptlength");
    if (this.view !== "Secretary") {
      cols.push("visitSource");
    }
    cols.push("funding", "examCode", "createdBy", "ExamStatus", "completed", "LastUpdate");
    // cols.push("createdBy", "Status", "LastUpdate");
    this.displayedColumns = cols;
  }

  private normalizeSource(src: any): VisitSource | "" {
    if (!src) {
      return "";
    }
    const visitSource = String(src).trim().toLowerCase().replace(/\s+/g, "_");
    if (visitSource === "whatsapp" || visitSource === "clinic_site" || visitSource === "direct") {
      return visitSource;
    }
    return "";
  }

  // Sorting

  private timeSort(aptTime: any): number {
    const date = aptTime instanceof Date ? aptTime : new Date(aptTime);
    const time = date?.getTime?.();
    return Number.isFinite(time) ? time : 0;
  }

  sortByAptTime(order: "asc" | "desc") {
    this.filteredAppointments.sort((a, b) => {
      const diff = this.timeSort(a.aptTime) - this.timeSort(b.aptTime);
      if (diff !== 0) return order === "asc" ? diff : -diff;
      return order === "asc"
        ? (a.AutoIndex ?? 0) - (b.AutoIndex ?? 0)
        : (b.AutoIndex ?? 0) - (a.AutoIndex ?? 0);
    });
    // Reassign to notify table of changes
    this.filteredAppointments = [...this.filteredAppointments];
  }

  sortByCreationTimeAllAppts(order: "asc" | "desc") {
    this.filteredAppointments.sort((a, b) => {
      const diff = this.timeSort(a.createdDate) - this.timeSort(b.createdDate);
      if (diff !== 0) return order === "asc" ? diff : -diff;
      return order === "asc"
        ? (a.AutoIndex ?? 0) - (b.AutoIndex ?? 0)
        : (b.AutoIndex ?? 0) - (a.AutoIndex ?? 0);
    });
    this.filteredAppointments = [...this.filteredAppointments];
  }

  private applyCurrentSort() {
    // Apply whichever sort the header indicates (default aptTime asc)
    if (!this.sortActive || !this.sortDirection) {
      this.sortActive = "aptTime";
      this.sortDirection = "asc";
    }
    if (this.sortActive === "aptTime") {
      this.sortByAptTime(this.sortDirection as "asc" | "desc");
    } else if (this.sortActive === "createdDate") {
      this.sortByCreationTimeAllAppts(this.sortDirection as "asc" | "desc");
    }
  }

  async getExamTypes() {
    try {
      const data = await this.settingsService.getExams();

      const mapped = (Array.isArray(data) ? data : []).map((type: any) => ({
        label: (type.examName ?? type.examType).toString().trim(),
        value: type.AutoIndex,
      }));

      this.examTypeOptions = [
        {
          label: this.translate.instant("appointments.gui.labels.appointment_stats.All"),
          value: "ALL",
        },
        ...mapped,
      ];
    } catch (err) {
      console.error("getExamTypes failed", err);
      this.examTypeOptions = [
        { label: this.translate.instant("appointments.gui.labels.appointment_stats.All"), value: "ALL" },
      ];
    }
  }

  private configurePaginatorLabels() {
    const prefix = "appointments.gui.labels.appointment_stats";
    this.paginatorIntl.itemsPerPageLabel = this.translate.instant(`${prefix}.Items per page`);
    this.paginatorIntl.nextPageLabel = this.translate.instant(`${prefix}.Next page`);
    this.paginatorIntl.previousPageLabel = this.translate.instant(`${prefix}.Previous page`);
    this.paginatorIntl.firstPageLabel = this.translate.instant(`${prefix}.First page`);
    this.paginatorIntl.lastPageLabel = this.translate.instant(`${prefix}.Last page`);
    this.paginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number) => {
      if (length === 0 || pageSize === 0) {
        return `0 ${this.translate.instant(`${prefix}.of`)} ${length}`;
      }
      const startIndex = page * pageSize;
      const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
      return `${startIndex + 1} - ${endIndex} ${this.translate.instant(`${prefix}.of`)} ${length}`;
    };
  }
}
